/**
 *
 * Route page for pages with a footer and navbar
 *
 */

import React, { Suspense, lazy, useEffect } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { useStateValue } from 'context/store';
import { logoutUser } from 'context/user/actions';
import { setProfileTab, setTab } from 'context/tabs/actions';

const AppContainer = styled.div`
  overflow: hidden;

  @media (max-width: 658px) {
    min-height: calc(100vh - 50px);
  }

  @media (min-width: 658px) {
    min-height: calc(100vh - 70px);
  }
`;

const AppWrapper = styled.div`
  @media (max-width: 658px) {
    margin-top: 50px;
    min-height: calc(100vh - 50px);
  }

  @media (min-width: 658px) {
    margin-top: 56px;
    min-height: calc(100vh - 70px);
  }
`;

const Terms = lazy(() => import(/* webpackChunkName: "Terms" */ './Terms'));
const Account = lazy(() =>
  import(/* webpackChunkName: "Account" */ './Account')
);
const Navbar = lazy(() =>
  import(/* webpackChunkName: "Navbar" */ 'components/navbar')
);
const Footer = lazy(() =>
  import(/* webpackChunkName: "Footer" */ 'components/footer')
);
const StorePage = lazy(() =>
  import(/* webpackChunkName: "StorePage" */ './StorePage')
);
const Promotions = lazy(() =>
  import(/* webpackChunkName: "Promotions" */ './Promotions')
);
const Gallery = lazy(() =>
  import(/* webpackChunkName: "Gallery" */ 'components/gallery')
);
const LandingPage = lazy(() =>
  import(/* webpackChunkName: "LandingPage" */ './LandingPage')
);
const CheckoutPage = lazy(() =>
  import(/* webpackChunkName: "CheckoutPage" */ './CheckoutPage')
);
const PaymentsPage = lazy(() =>
  import(/* webpackChunkName: "PaymentsPage" */ './PaymentsPage')
);
const SearchResults = lazy(() =>
  import(/* webpackChunkName: "SearchResults" */ './SearchResults')
);
const EventItemPage = lazy(() =>
  import(/* webpackChunkName: "EventItemPage" */ './EventItemPage')
);
const MusicItemPage = lazy(() =>
  import(/* webpackChunkName: "MusicItemPage" */ './MusicItemPage')
);
const LSPage = lazy(() =>
  import(/* webpackChunkName: "Promotions" */ 'containers/LiveStream/LSPage')
);
const CompletePayment = lazy(() =>
  import(/* webpackChunkName: "CompletePayment" */ './CompletePayment')
);
const NotFoundPage = lazy(() =>
  import(/* webpackChunkName: "PersonalSettingsPage" */ './NotFoundPage')
);

function Routes(props) {
  const token = localStorage.getItem('userToken');
  const userProfile = localStorage.getItem('user');

  const {
    state: { user },
    dispatch
  } = useStateValue();
  const { isAuthenticated } = user;

  let accountPath = props.location.pathname.includes('/account/');

  useEffect(() => {
    if (userProfile && !token) {
      dispatch(logoutUser());
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    switch (props.location.pathname) {
      case '/featured':
        return dispatch(setTab(0));
      case '/music':
        return dispatch(setTab(1));
      case '/events':
        return dispatch(setTab(2));
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      switch (props.location.pathname) {
        case '/account/streams':
          return dispatch(setProfileTab(0));
        case '/account/tickets':
          return dispatch(setProfileTab(1));
        case '/account/library':
          return dispatch(setProfileTab(2));
        case '/account/profile':
          return dispatch(setProfileTab(3));
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.pathname]);

  if (accountPath && !isAuthenticated) {
    return (
      <Redirect
        to={{
          pathname: '/login',
          state: { referrer: props.location.pathname }
        }}
      />
    );
  }

  return (
    <AppContainer>
      <Navbar />
      <Gallery />
      <AppWrapper>
        <Suspense fallback={<p>{null}</p>}>
          <Switch>
            <Route exact path="/" component={LandingPage} />
            <Route exact path="/home" component={LandingPage} />
            <Route exact path="/featured" component={LandingPage} />
            <Route exact path="/music" component={LandingPage} />
            <Route exact path="/events" component={LandingPage} />
            <Route exact path="/checkout" component={CheckoutPage} />
            <Route exact path="/payments" component={PaymentsPage} />
            <Route exact path="/event/:slug" component={EventItemPage} />
            <Route exact path="/album/:slug" component={MusicItemPage} />
            <Route exact path="/stream/:id" component={LSPage} />
            <Route exact path="/store/event/:slug" component={StorePage} />
            <Route exact path="/store/music/:slug" component={StorePage} />
            <Route exact path="/promo/:slug" component={Promotions} />
            <Route
              exact
              path="/payment/complete/:id"
              component={CompletePayment}
            />

            <Route exact path="/account/:slug" component={Account} />
            <Route exact path="/search" component={SearchResults} />
            <Route exact path="/terms" component={Terms} />
            <Route component={NotFoundPage} />
          </Switch>
        </Suspense>
      </AppWrapper>
      <Footer />
    </AppContainer>
  );
}

export default Routes;
